<template>
    <div class="col-12">
        <div class="row py-2">

            <!-- Product -->
            <div class="col-md-4 col-12">
                <label class="form-label">Product</label>
                <AsyncSelect
                    v-model="data.product"
                    placeholder="Select Product"
                    :format-label="option => option.text"
                    :api-service="fetchProductList"
                    :additional-query="additionalQuery"
                    :additional-option="additionalOptions"
                    label="text"
                    ref="productField"
                    :disabled="isUpdate"
                />
            </div>

            <!-- Purchase Rate -->
            <div class="col-md-2 col-12">
                <label class="form-label">
                    P. Rate <span class="text-danger">*</span>
                </label>
                <input
                    v-model.number="data.rate"
                    type="number"
                    min="0"
                    required
                    class="form-control text-end"
                />
            </div>

            <!-- Quantity -->
            <div class="col-md-2 col-12">
                <label class="form-label">
                    Quantity <span class="text-danger">*</span>
                </label>
                <input
                    v-model.number="data.quantity"
                    type="number"
                    min="1"
                    required
                    class="form-control text-end"
                    @keyup.enter="handleAddProduct"
                />
            </div>

            <!-- Amount -->
            <div class="col-md-2 col-12">
                <label class="form-label">Amount</label>
                <input
                    :value="amount"
                    readonly
                    type="number"
                    class="form-control text-end"
                />
            </div>

            <!-- Add Button -->
            <div class="col-md-2 col-12 d-flex align-items-end">
                <button
                    class="btn btn-primary w-100"
                    @click="handleAddProduct"
                    :disabled="!canSubmit"
                >
                    Add
                </button>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRoute } from "vue-router";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handlePurchaseAndSales from "@/services/modules/purchase";

const props = defineProps({
    isUpdate: Boolean
});

const emit = defineEmits(["handleProductAdd"]);

const route = useRoute();
const { fetchProductList, fetchProductStock } = handlePurchaseAndSales();

const productField = ref(null);
const updateIndex = ref(null);
const isEdit = ref(false);

const additionalQuery = {
    product_type: "finished_goods"
};

const emptyData = {
    product: null,
    product_id: null,
    product_name: "",
    rate: 0,
    quantity: 0
};

const data = ref({ ...emptyData });

const companyQuery = `?company_id=${route.params.companyId}`;

const amount = computed(() => {
    return (data.value.rate * data.value.quantity).toFixed(4);
});

const canSubmit = computed(() => {
    return data.value.rate > 0 && data.value.quantity > 0;
});

const additionalOptions = computed(() => {
    if (!route.params.purchaseId || !data.value.product) return;
    return [
        {
            id: data.value.product_id,
            text: data.value.product.name
        }
    ];
});

/**
 * Load purchase rate on product change
 */
watch(() => data.value.product, async (product) => {
    if (!product || !product.id) return;

    try {
        const res = await fetchProductStock(
            product.id,
            `${companyQuery}&skip_stock_check=1`
        );

        const currentRate =
            data.value.rate && data.value.rate > 0 ? data.value.rate : 0;

        const responseRate =
            res.data &&
            res.data.purchase_rate
                ? res.data.purchase_rate
                : 0;

        data.value.product_id = product.id;
        data.value.product_name = product.text;
        data.value.rate = isEdit.value ? currentRate : responseRate;
        isEdit.value = false;
    } catch (_) {}
});

const focusProduct = () => {
    if (
        productField.value &&
        productField.value.dropdownRef &&
        productField.value.dropdownRef.searchEl
    ) {
        productField.value.dropdownRef.searchEl.focus();
    }
};

const handleAddProduct = () => {
    if (!canSubmit.value) return;

    emit("handleProductAdd", { ...data.value }, updateIndex.value);
    data.value = { ...emptyData };
    updateIndex.value = null;
    focusProduct();
};

const setData = (payload) => {
    if (payload) {
        isEdit.value = payload.isEdit ?? false;
        delete payload.isEdit;
        data.value = { ...payload };
    }
};

defineExpose({ updateIndex, setData });
</script>
